import React, { useState } from "react";
import {
  Box,
  Chip,
  Collapse,
  IconButton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import {
  Close as CloseIcon,
  ExpandLess,
  ExpandMore,
  Label as LabelIcon,
  Person as PersonIcon,
  Tune as TuneIcon,
  Sort as SortIcon,
} from "@mui/icons-material";
import { Priority } from "hooks/usePriorities";
import { ServerTag } from "model/activitiesModel";
import { ProcessModelType } from "model/processModel";
import { getTranslation } from "common";
import { SortCriterion } from "common/utilities";
import { SortState } from "hooks/useSortableList";

interface MyActivitiesMobileFiltersProps {
  searchText: string;
  setSearchText: (v: string) => void;
  searchTags: number[];
  setSearchTags: (tags: number[]) => void;
  selectedPriorities: string[];
  setSelectedPriorities: (priorities: string[]) => void;
  selectedModels: number[];
  setSelectedModels: (models: number[]) => void;
  selectedUserDataKey: string | null;
  handleSelectUserDataKey: (key: string | null) => void;
  setSortCriteria: (criteria: SortCriterion) => void;
  handleClearFilters: () => void;
  processModels: ProcessModelType[];
  tagList: ServerTag[];
  priorities: Priority[];
  userDataKeys: string[];
  translations: Record<string, string>;
  /** Number of currently active filters (for the header count). */
  activeCount: number;
}

/**
 * Mobile filter section. Renders a tap-to-expand "Filtri" header
 * plus a stack of `FilterGroup` accordions (User Data, Tag, Priority,
 * Process Model, Sort). Groups are larger and use a bigger font than
 * before so they read comfortably on a phone.
 */
const MyActivitiesMobileFilters: React.FC<MyActivitiesMobileFiltersProps> = ({
  searchText,
  setSearchText,
  searchTags,
  setSearchTags,
  selectedPriorities,
  setSelectedPriorities,
  selectedModels,
  setSelectedModels,
  selectedUserDataKey,
  handleSelectUserDataKey,
  setSortCriteria,
  handleClearFilters,
  processModels,
  tagList,
  priorities,
  userDataKeys,
  translations,
  activeCount,
}) => {
  const theme = useTheme();
  const [open, setOpen] = useState(true);
  const [userDataOpen, setUserDataOpen] = useState(false);
  const [tagOpen, setTagOpen] = useState(false);
  const [priorityOpen, setPriorityOpen] = useState(false);
  const [modelOpen, setModelOpen] = useState(false);
  const [sortOpen, setSortOpen] = useState(false);

  const handleTogglePriority = (name: string) => {
    if (selectedPriorities.includes(name)) {
      setSelectedPriorities(selectedPriorities.filter((p) => p !== name));
    } else {
      setSelectedPriorities([...selectedPriorities, name]);
    }
  };

  const handleToggleModel = (id: number) => {
    if (selectedModels.includes(id)) {
      setSelectedModels(selectedModels.filter((m) => m !== id));
    } else {
      setSelectedModels([...selectedModels, id]);
    }
  };

  const handleToggleTag = (id: number) => {
    if (searchTags.includes(id)) {
      setSearchTags(searchTags.filter((t) => t !== id));
    } else {
      setSearchTags([...searchTags, id]);
    }
  };

  return (
    <Box
      sx={{
        mt: 1.25,
        pt: 1.25,
        borderTop: "1px solid",
        borderColor: "divider",
        bgcolor: (t) => (t.palette.mode === "dark" ? "background.default" : "grey.50"),
        borderRadius: 2,
        px: 1.25,
        pb: 1,
      }}
    >
      {/* Filter section header (always visible while the panel is open). */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ color: "text.primary" }}>
          <TuneIcon sx={{ fontSize: 18, color: "primary.main" }} />
          <Typography
            variant="subtitle2"
            sx={{ fontSize: 13, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.6 }}
          >
            {getTranslation(translations, "generic.filters.caption")}
          </Typography>
          {activeCount > 0 && (
            <Chip
              label={activeCount}
              size="small"
              sx={{
                height: 20,
                fontSize: 11,
                fontWeight: 700,
                bgcolor: "primary.main",
                color: "common.white",
                "& .MuiChip-label": { px: 0.85 },
              }}
            />
          )}
        </Stack>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          {activeCount > 0 && (
            <IconButton
              size="small"
              onClick={handleClearFilters}
              aria-label="clear filters"
              sx={{ p: 0.75 }}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          )}
          <IconButton
            size="small"
            onClick={() => setOpen((o) => !o)}
            aria-label="expand filters"
            sx={{ p: 0.75 }}
          >
            {open ? <ExpandLess sx={{ fontSize: 20 }} /> : <ExpandMore sx={{ fontSize: 20 }} />}
          </IconButton>
        </Stack>
      </Stack>

      <Collapse in={open}>
        <Stack spacing={1.25}>
          {/* Search chip (echoes header search) */}
          {searchText && (
            <Chip
              label={`"${searchText}"`}
              onDelete={() => setSearchText("")}
              sx={{ alignSelf: "flex-start", fontSize: 12, height: 26, "& .MuiChip-label": { px: 1 } }}
            />
          )}

          {/* User data key filter */}
          {userDataKeys.length > 0 && (
            <FilterGroup
              icon={<PersonIcon sx={{ fontSize: 16 }} />}
              label={getTranslation(translations, "generic.userdata.caption")}
              open={userDataOpen}
              onToggle={() => setUserDataOpen((o) => !o)}
              accentColor={theme.palette.primary.main}
            >
              <BigChip
                label="—"
                onClick={() => handleSelectUserDataKey(null)}
                selected={selectedUserDataKey === null}
              />
              {userDataKeys.map((key) => (
                <BigChip
                  key={key}
                  label={key}
                  onClick={() => handleSelectUserDataKey(key)}
                  selected={selectedUserDataKey === key}
                />
              ))}
            </FilterGroup>
          )}

          {/* Tag filter */}
          {tagList.length > 0 && (
            <FilterGroup
              icon={<LabelIcon sx={{ fontSize: 16 }} />}
              label={getTranslation(translations, "generic.filter.by.tag")}
              open={tagOpen}
              onToggle={() => setTagOpen((o) => !o)}
              accentColor={theme.palette.primary.main}
            >
              {tagList.map((tag) => (
                <BigChip
                  key={tag.idtag}
                  label={tag.tag}
                  onClick={() => handleToggleTag(tag.idtag)}
                  selected={searchTags.includes(tag.idtag)}
                />
              ))}
            </FilterGroup>
          )}

          {/* Priority filter */}
          {priorities.length > 0 && (
            <FilterGroup
              icon={<TuneIcon sx={{ fontSize: 16 }} />}
              label={getTranslation(translations, "generic.priorities.caption")}
              open={priorityOpen}
              onToggle={() => setPriorityOpen((o) => !o)}
              accentColor={theme.palette.primary.main}
            >
              {priorities.map((p) => (
                <BigChip
                  key={p.id}
                  label={p.name}
                  onClick={() => handleTogglePriority(p.name)}
                  selected={selectedPriorities.includes(p.name)}
                />
              ))}
            </FilterGroup>
          )}

          {/* Process model filter */}
          {processModels.length > 0 && (
            <FilterGroup
              icon={<TuneIcon sx={{ fontSize: 16 }} />}
              label={getTranslation(translations, "generic.filter.by.processmodel")}
              open={modelOpen}
              onToggle={() => setModelOpen((o) => !o)}
              accentColor={theme.palette.primary.main}
            >
              {processModels.map((m) => (
                <BigChip
                  key={m.id}
                  label={m.name}
                  onClick={() => handleToggleModel(m.id)}
                  selected={selectedModels.includes(m.id)}
                />
              ))}
            </FilterGroup>
          )}

          {/* Sort quick toggle */}
          <FilterGroup
            icon={<SortIcon sx={{ fontSize: 16 }} />}
            label={getTranslation(translations, "generic.sort.caption")}
            open={sortOpen}
            onToggle={() => setSortOpen((o) => !o)}
            accentColor={theme.palette.primary.main}
          >
            {[
              { field: "name", key: "generic.sort.by.process_name" },
              { field: "priority", key: "generic.sort.by.priority" },
              { field: "startDate", key: "generic.sort.by.startdate" },
            ].map((s) => (
              <BigChip
                key={s.field}
                label={getTranslation(translations, s.key)}
                onClick={() => setSortCriteria({ [s.field]: SortState.Asc })}
              />
            ))}
          </FilterGroup>
        </Stack>
      </Collapse>
    </Box>
  );
};

interface FilterGroupProps {
  icon: React.ReactNode;
  label: string;
  open: boolean;
  onToggle: () => void;
  accentColor: string;
  children: React.ReactNode;
}

const FilterGroup: React.FC<FilterGroupProps> = ({
  icon,
  label,
  open,
  onToggle,
  accentColor,
  children,
}) => (
  <Box>
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      onClick={onToggle}
      sx={{
        cursor: "pointer",
        py: 1,
        px: 1,
        borderRadius: 1.5,
        bgcolor: (t) => (open ? "action.hover" : "transparent"),
        "&:hover": { bgcolor: "action.hover" },
      }}
    >
      {icon}
      <Typography
        sx={{
          fontSize: 13,
          fontWeight: 800,
          textTransform: "uppercase",
          letterSpacing: 0.6,
          color: "text.primary",
          flex: 1,
        }}
      >
        {label}
      </Typography>
      {open ? (
        <ExpandLess sx={{ fontSize: 20, color: "text.secondary" }} />
      ) : (
        <ExpandMore sx={{ fontSize: 20, color: "text.secondary" }} />
      )}
    </Stack>
    <Collapse in={open}>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 0.75,
          pt: 1,
          pb: 0.5,
          pl: 1.5,
          borderTop: "1px dashed",
          borderColor: "divider",
        }}
      >
        {children}
      </Box>
    </Collapse>
    <Box sx={{ display: "none" }}>{accentColor}</Box>
  </Box>
);

/**
 * Larger filter chip used inside the expanded filter groups on mobile.
 * Compared to the previous "size=small" MUI chip this is ~30% bigger
 * so the labels are easier to read on phones, with more padding and
 * a clearer selected/unselected visual contrast.
 */
const BigChip: React.FC<{
  label: string;
  onClick: () => void;
  selected?: boolean;
}> = ({ label, onClick, selected }) => (
  <Chip
    label={label}
    onClick={onClick}
    color={selected ? "primary" : "default"}
    variant={selected ? "filled" : "outlined"}
    sx={{
      height: 30,
      fontSize: 12,
      fontWeight: 700,
      borderRadius: 2,
      "& .MuiChip-label": { px: 1.25 },
      m: 0,
    }}
  />
);

export default MyActivitiesMobileFilters;
